/**
 * Typed fetch client for the ClawHire REST API (`/api/v1`, see docs/api-contract.md).
 *
 * - Base URL: `VITE_API_URL` (origin) or same origin; `/api/v1` is appended.
 * - The access token lives in memory and in `sessionStorage` (survives a reload in the same tab).
 * - Every request sends `Authorization: Bearer <token>` and `credentials: 'include'` so the
 *   httpOnly refresh cookie travels with `/auth/*`.
 * - On a 401 the client calls `POST /auth/refresh` once (single-flight across concurrent
 *   requests) and retries the original request. If the refresh fails the token is cleared and
 *   `onUnauthorized` listeners are notified (the AuthProvider redirects to /login).
 * - Errors are thrown as `ApiError { status, code, message, details, requestId }`.
 *
 * Usage:
 *   const page = await api.get<Page<Position>>('/positions', { query: { stage: ['NEW', 'SOURCING'] } });
 *   await api.post('/activity', { entityType: 'POSITION', entityId, text });
 *   await api.upload('/files/upload', formData);
 */

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | readonly QueryValue[]>;

export interface ApiErrorBody {
  statusCode?: number;
  error?: string;
  message?: string | string[];
  code?: string;
  details?: unknown;
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody | string | null | undefined) {
    const parsed = typeof body === 'object' && body !== null ? body : {};
    const rawMessage = typeof body === 'string' ? body : parsed.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(', ')
      : rawMessage || parsed.error || defaultMessage(status);
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = parsed.code ?? parsed.error?.toUpperCase().replace(/\s+/g, '_') ?? `HTTP_${status}`;
    this.details = parsed.details;
    this.requestId = parsed.requestId;
  }

  /** zod issues from a 400 VALIDATION error as `{ path: message }`, for form field errors. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const issue of this.details as Array<{ path?: unknown; message?: unknown }>) {
        const path = Array.isArray(issue.path) ? issue.path.join('.') : String(issue.path ?? '');
        if (path && typeof issue.message === 'string' && !(path in out)) out[path] = issue.message;
      }
    }
    return out;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function defaultMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request';
    case 401:
      return 'Not signed in';
    case 403:
      return 'Not allowed';
    case 404:
      return 'Not found';
    case 409:
      return 'Conflict';
    case 0:
      return 'Network error';
    default:
      return `Request failed (${status})`;
  }
}

// ---------------------------------------------------------------------------
// Query string

/**
 * Builds a query string; arrays are repeated (`stage=NEW&stage=SOURCING`), `null`, `undefined`
 * and empty strings are skipped. Returns '' or a string starting with '?'.
 */
export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    const values = Array.isArray(value) ? value : [value];
    for (const v of values as readonly QueryValue[]) {
      if (v === null || v === undefined || v === '') continue;
      search.append(key, String(v));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------------------
// Token store

const TOKEN_KEY = 'clawhire.accessToken';
let accessToken: string | null = null;
let tokenLoaded = false;

function readStoredToken(): string | null {
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  if (!tokenLoaded) {
    accessToken = readStoredToken();
    tokenLoaded = true;
  }
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  tokenLoaded = true;
  try {
    if (token) window.sessionStorage.setItem(TOKEN_KEY, token);
    else window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Called when a refresh fails (session ended). Returns an unsubscribe function. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function notifyUnauthorized(): void {
  for (const listener of unauthorizedListeners) listener();
}

// ---------------------------------------------------------------------------
// Core request

const API_ORIGIN = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
export const API_BASE = `${API_ORIGIN}/api/v1`;

export interface RequestOptions {
  query?: QueryParams;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip the automatic refresh + retry on 401 (used for the auth endpoints themselves). */
  noRefresh?: boolean;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** Absolute URL for an API path, e.g. for `<a download>` links. */
export function apiUrl(path: string, query?: QueryParams): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${p}${buildQuery(query)}`;
}

export interface AuthResponse {
  accessToken: string;
  user: unknown;
}

let refreshPromise: Promise<string | null> | null = null;

/** POST /auth/refresh (cookie based). Concurrent callers share one request. */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(apiUrl('/auth/refresh'), {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          setAccessToken(null);
          return null;
        }
        const body = (await res.json()) as AuthResponse;
        setAccessToken(body.accessToken);
        return body.accessToken;
      } catch {
        setAccessToken(null);
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205) return undefined;
  const type = res.headers.get('content-type') ?? '';
  const text = await res.text();
  if (!text) return undefined;
  if (type.includes('json')) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

async function doFetch(
  method: Method,
  path: string,
  init: RequestInit,
  opts: RequestOptions,
): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = getAccessToken();
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  for (const [k, v] of Object.entries(opts.headers ?? {})) headers.set(k, v);
  try {
    return await fetch(apiUrl(path, opts.query), {
      ...init,
      method,
      headers,
      credentials: 'include',
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, { message: err instanceof Error ? err.message : 'Network error', code: 'NETWORK' });
  }
}

async function request<T>(
  method: Method,
  path: string,
  init: RequestInit,
  opts: RequestOptions = {},
): Promise<T> {
  let res = await doFetch(method, path, init, opts);

  if (res.status === 401 && !opts.noRefresh) {
    const token = await refreshAccessToken();
    if (token) {
      res = await doFetch(method, path, init, opts);
    } else {
      notifyUnauthorized();
    }
  }

  const body = await parseBody(res);
  if (!res.ok) {
    throw new ApiError(res.status, body as ApiErrorBody | string | undefined);
  }
  return body as T;
}

function jsonInit(body: unknown): RequestInit {
  if (body === undefined) return {};
  return { body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } };
}

export const api = {
  get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return request<T>('GET', path, {}, opts);
  },
  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return request<T>('POST', path, jsonInit(body), opts);
  },
  patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, jsonInit(body), opts);
  },
  put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, jsonInit(body), opts);
  },
  delete<T = void>(path: string, opts?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, {}, opts);
  },
  /** Multipart upload; the browser sets the boundary, so no Content-Type here. */
  upload<T>(path: string, formData: FormData, opts?: RequestOptions): Promise<T> {
    return request<T>('POST', path, { body: formData }, opts);
  },
  /** Binary download (e.g. `/files/:id/content`) with auth; returns a Blob. */
  async blob(path: string, opts?: RequestOptions): Promise<Blob> {
    const o: RequestOptions = { ...opts, headers: { Accept: '*/*', ...(opts?.headers ?? {}) } };
    let res = await doFetch('GET', path, {}, o);
    if (res.status === 401 && !o.noRefresh) {
      const token = await refreshAccessToken();
      if (token) res = await doFetch('GET', path, {}, o);
      else notifyUnauthorized();
    }
    if (!res.ok) throw new ApiError(res.status, (await parseBody(res)) as ApiErrorBody | string | undefined);
    return res.blob();
  },
  url: apiUrl,
  buildQuery,
};

export type Api = typeof api;

/** Reset the client state (tests). */
export function __resetApiForTests(): void {
  accessToken = null;
  tokenLoaded = false;
  refreshPromise = null;
  unauthorizedListeners.clear();
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
